// Utils for the PostComposer
import DOMPurify from "dompurify";

// Simple group id (timestamp + rand). Replace with robust uid if needed
export function generateGroupId() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

async function fetchYouTubeAspectRatio(videoId) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `https://www.youtube.com/oembed?format=json&url=https://www.youtube.com/shorts/${encodeURIComponent(videoId)}`,
      { signal: controller.signal }
    );
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = await res.json();
    if (typeof data.width === "number" && typeof data.height === "number") {
      return data.height > data.width;
    }
    return null;
  } catch {
    return null;
  }
}

export function isVerticalEmbed(item) {
  if (!item || item.mediaType !== "embed" || item.embedProvider !== "youtube") return false;
  if (item.embedIsVertical === true) return true;
  return /shorts\//i.test(item.embedUrl || "");
}

export function isYouTubeMusicUrl(url) {
  return /music\.youtube\.com\//i.test(url || "");
}

export function isSquareEmbed(item) {
  if (!item || item.mediaType !== "embed") return false;
  if (item.embedIsSquare === true) return true;
  // Check embedUrl, itemName (often the pasted URL), and title for music.youtube.com
  return (
    isYouTubeMusicUrl(item.embedUrl || "") ||
    isYouTubeMusicUrl(item.itemName || "") ||
    isYouTubeMusicUrl(item.title || "")
  );
}

// Very light oEmbed detector: return provider + sanitized HTML or null
// NOTE: In v1 we only support YouTube and SoundCloud via their standard embed patterns.
export async function detectEmbed(url) {
  try {
    console.log("[detectEmbed] called with", url);
    // Match all YouTube domains: youtube.com, music.youtube.com, m.youtube.com, youtu.be, etc.
    const YT = /^(https?:\/\/)?([\w-]+\.)*youtube\.com\//i;
    const YT_SHORT = /^(https?:\/\/)?(www\.)?youtu\.be\//i;
    const SC = /^(https?:\/\/)?(www\.)?soundcloud\.com\//i;
    const SPOTIFY = /^(https?:\/\/)?(open\.)?spotify\.com\//i;

    if (YT.test(url) || YT_SHORT.test(url)) {
      // Normalize to privacy-enhanced embed iframe (youtube-nocookie.com)
      const videoId = extractYouTubeId(url);
      console.log("[detectEmbed] YouTube matched, videoId:", videoId);
      if (videoId) {
        const html = `<iframe src="https://www.youtube-nocookie.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen loading="lazy"></iframe>`;
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        let embedIsVertical = await fetchYouTubeAspectRatio(videoId);
        if (embedIsVertical === null) embedIsVertical = /\/shorts\//i.test(url);
        const embedIsSquare = isYouTubeMusicUrl(url);
        if (embedIsSquare) embedIsVertical = false;
        console.log("[detectEmbed] returning YouTube embed", { videoId, htmlLen: html.length, thumbnailUrl, embedIsVertical, embedIsSquare });
        return { provider: "youtube", html, thumbnailUrl, meta: { title: `YouTube: ${videoId}`, videoId }, embedIsVertical: !!embedIsVertical, embedIsSquare };
      }
      // No video ID — try playlist
      const playlistId = extractYouTubePlaylistId(url);
      if (playlistId) {
        console.log("[detectEmbed] YouTube playlist matched, playlistId:", playlistId);
        const html = `<iframe src="https://www.youtube-nocookie.com/embed/videoseries?list=${playlistId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen loading="lazy"></iframe>`;
        return { provider: "youtube", html, thumbnailUrl: "", meta: { title: `YouTube Playlist`, playlistId } };
      }
      return { provider: "youtube", meta: { title: url } };
    }
    if (SC.test(url)) {
      // Strip query params (si, utm_*, etc.) — SoundCloud player rejects URLs with them
      // Keep the path intact (includes /s-TOKEN for private tracks)
      let cleanUrl = url;
      try {
        const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
        cleanUrl = `${parsed.origin}${parsed.pathname}`;
      } catch { /* use original if parsing fails */ }
      const encoded = encodeURIComponent(cleanUrl);
      const html = `<iframe width="100%" height="166" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=${encoded}"></iframe>`;
      return { provider: "soundcloud", html, meta: { title: `SoundCloud` } };
    }

    if (SPOTIFY.test(url)) {
      // Support track, album and playlist embeds. Normalize URL to embed form.
      try {
        const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
        // Path format: /track/{id}, /album/{id}, /playlist/{id}
        const parts = parsed.pathname.split("/").filter(Boolean);
        if (/^intl-[a-z]{2}(?:-[A-Z]{2})?$/i.test(parts[0] || "")) parts.shift();
        const kind = parts[0];
        const id = parts[1];
        const supportedKinds = new Set(["track", "album", "playlist", "artist", "show", "episode"]);
        if (supportedKinds.has(kind) && id) {
          // Compact (152) for single tracks / episodes; full card (352) for collections.
          const height = kind === "track" || kind === "episode" ? 152 : 352;
          const html = `<iframe style="border-radius:12px" src="https://open.spotify.com/embed/${kind}/${id}" width="100%" height="${height}" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
          return { provider: "spotify", html, meta: { title: `Spotify: ${kind}/${id}` }, embedIsSpotify: true, embedSpotifyKind: kind };
        }
      } catch (e) {
        /* fall through */
      }
      return { provider: "spotify", meta: { title: url } };
    }

    // Fallback: no embed, return minimal meta
    return { provider: null, meta: { title: url } };
  } catch (e) {
    console.warn("detectEmbed failed", e);
    return null;
  }
}

export function sanitizeEmbedHtml(html) {
  try {
    return DOMPurify.sanitize(html || "", {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "allowtransparency",
        "frameborder",
        "src",
        "loading",
        "referrerpolicy",
        "scrolling",
        "style",
        "width",
        "height",
      ],
    });
  } catch (e) {
    console.warn("sanitizeEmbedHtml failed", e);
    return "";
  }
}

export function injectYouTubeApiParams(html) {
  if (!html || !/youtube/i.test(html)) return html;
  try {
    return html.replace(/src="([^"]+)"/i, (match, src) => {
      if (/enablejsapi/i.test(src)) return match;
      const sep = src.includes("?") ? "&" : "?";
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      return `src="${src}${sep}enablejsapi=1&origin=${encodeURIComponent(origin)}"`;
    });
  } catch {
    return html;
  }
}

export function extractYouTubeId(url) {
  try {
    // Supports: youtu.be/ID, watch?v=ID, embed/ID, shorts/ID, live/ID
    const patterns = [
      /youtu\.be\/([\w-]{11})/i,
      /[?&]v=([\w-]{11})/i,
      /embed\/([\w-]{11})/i,
      /shorts\/([\w-]{11})/i,
      /live\/([\w-]{11})/i,
    ];
    for (const re of patterns) {
      const m = url.match(re);
      if (m && m[1]) return m[1];
    }
    return null;
  } catch {
    return null;
  }
}

function extractYouTubePlaylistId(url) {
  try {
    const m = url.match(/[?&]list=([\w-]+)/i);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}
